import { MainTracker } from "../../PixelTrackers/MainTracker";
import {
  WlReorderVariant,
  WL_REORDER_INTENT_PARAM,
  WL_REORDER_SUBSCRIPTION_INTENT,
  getWlReorderTrackingProps,
  toWlReorderTestEventValue,
  WL_REORDER_TEST_EVENT,
} from "../../utils/wlReorderSubscriptionExperiment";
import { ReorderTrackingContext } from "./reorderTypes";

const REORDER_SOURCE_PAGE_MAP: Record<string, string> = {
  hp: "home_page",
  cp: "category_page",
  ma: "my_account",
};

export const MY_ACCOUNT_SOURCE_PAGE = "ma";

/**
 * Extract reorder tracking params from the current URL.
 */
const getUrlParams = () => {
  const urlParams = new URLSearchParams(window.location.search);
  return {
    sp: urlParams.get("sp"),
    isReorder: urlParams.get("reorder") === "true",
    isSaved: urlParams.get("saved") === "true",
    cat: urlParams.get("cat"),
    intent: urlParams.get(WL_REORDER_INTENT_PARAM),
  };
};

/**
 * Return reorder tracking fields extracted from the current URL.
 */
export const getReorderPayloadExtras = (): Record<string, string> => {
  const extras: Record<string, string> = {};
  const { sp, isReorder, isSaved } = getUrlParams();
  if (sp) extras.reorderSourcePage = sp;
  if (isReorder) extras.reorderFlag = "true";
  if (isSaved) extras.savedFlag = "true";
  return extras;
};

/**
 * Build the API query string for the reorder endpoint (?productId=...&apr=...).
 */
export const buildReorderApiQuery = (searchParams: URLSearchParams): string => {
  const params = new URLSearchParams();
  const productId = searchParams.get('productId');
  const apr = searchParams.get('apr');
  if (productId) params.set('productId', productId);
  if (apr) params.set('apr', apr);
  return params.toString() ? `?${params.toString()}` : '';
};

/**
 * Build a reorder params object for carrying through navigation (e.g. shipping → payment).
 */
export const getReorderNavigationParams = (): Record<string, string> => {
  const params: Record<string, string> = { reorder: "true" };
  const { sp, intent } = getUrlParams();
  if (sp) params.sp = sp;
  if (intent) params[WL_REORDER_INTENT_PARAM] = intent;
  return params;
};

/** My Account reorder URL; `subscriptionIntent` = B CTA (Care+ plan only). */
export const buildMyAccountReorderUrl = (
  orderHashID: string,
  country: string,
  language: string,
  { subscriptionIntent = false }: { subscriptionIntent?: boolean } = {}
) => {
  const params = new URLSearchParams({ sp: MY_ACCOUNT_SOURCE_PAGE });
  if (subscriptionIntent) params.set(WL_REORDER_INTENT_PARAM, WL_REORDER_SUBSCRIPTION_INTENT);
  return {
    pathname: `/reorder/${orderHashID}/${country}/${language}/medical_disclaimer/`,
    search: `?${params.toString()}`,
  };
};

/**
 * Fire "Reorder Paid" Mixpanel event.
 * Reads reorder params (sp, reorder, saved) from the current URL.
 * Works for both dropin and 3DS redirect flows since params are carried in the return URL.
 */
export const trackReorderPaid = (
  orderAmount: number | string,
  category: string
) => {
  const { sp, isReorder, isSaved, cat } = getUrlParams();
  if (!sp || (!isReorder && !isSaved)) return;

  MainTracker.track("custom", "Reorder Paid", {
    order_amount: orderAmount,
    source: REORDER_SOURCE_PAGE_MAP[sp] || sp,
    category: category || cat || "unknown",
    reorder_type: isReorder ? "reorder" : "saved_order",
  });
};

// Reorder funnel events (all reorders; `WL Test` set only inside the test).

export const REORDER_EVENTS = {
  SWITCH_TO_SUBSCRIPTION: "Funnel Switch To Subscription Button",
  START_REORDER_FLOW: "Funnel Start Reorder Flow",
  REORDER_FLOW_SCREEN: "Funnel Reorder Flow Screen",
  REORDER_FLOW_END: "Funnel Reorder Flow End",
} as const;

export const buildReorderEventProps = (
  context: ReorderTrackingContext | null | undefined,
  medicationPrice: number,
  variant: WlReorderVariant | null | undefined
) => ({
  Category: context?.category ?? null,
  "Product Catalog": context?.productCatalog ?? null,
  "Product Item": context?.productItem ?? null,
  "Category Purchase Number": context?.categoryPurchaseNumber ?? null,
  "Medication Price": Math.round(medicationPrice * 100) / 100,
  ...getWlReorderTrackingProps(variant),
});

const trackReorderEvent = (
  eventName: string,
  context: ReorderTrackingContext | null | undefined,
  medicationPrice: number,
  variant: WlReorderVariant | null | undefined,
  extra: Record<string, unknown> = {}
) => {
  MainTracker.track("custom", eventName, { ...buildReorderEventProps(context, medicationPrice, variant), ...extra });
};

/** My Orders landing of an eligible customer, all arms. */
export const trackReorderWlTest = (variant: WlReorderVariant) =>
  MainTracker.track("custom", WL_REORDER_TEST_EVENT, { test: toWlReorderTestEventValue(variant) });

/** My Account B CTA click. */
export const trackSwitchToSubscriptionClick = (context: ReorderTrackingContext | null | undefined, medicationPrice: number, variant: WlReorderVariant | null) =>
  trackReorderEvent(REORDER_EVENTS.SWITCH_TO_SUBSCRIPTION, context, medicationPrice, variant);

/** First reorder screen. */
export const trackStartReorderFlow = (context: ReorderTrackingContext | null | undefined, medicationPrice: number, variant: WlReorderVariant | null) =>
  trackReorderEvent(REORDER_EVENTS.START_REORDER_FLOW, context, medicationPrice, variant);

/** Treatment screen. */
export const trackReorderFlowScreen = (context: ReorderTrackingContext | null | undefined, medicationPrice: number, variant: WlReorderVariant | null) =>
  trackReorderEvent(REORDER_EVENTS.REORDER_FLOW_SCREEN, context, medicationPrice, variant);

/** Treatment screen CTA; price of the picked option. */
export const trackReorderFlowEnd = (
  context: ReorderTrackingContext | null | undefined,
  medicationPrice: number,
  variant: WlReorderVariant | null,
  isSubscription: boolean
) =>
  trackReorderEvent(REORDER_EVENTS.REORDER_FLOW_END, context, medicationPrice, variant, {
    "Subscription/Plan": isSubscription ? "Yes" : "No",
  });
